import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';

const SOURCE_LABELS = {
  market: 'Market Price',
  shopify_active: 'Retail listing average',
  shopify_sold: 'Retail sold average',
  ebay_active: 'eBay listing average',
  ebay_sold: 'eBay ended listings average',
  tcgplayer: 'TCG Player'
};

const SOURCE_COLORS = {
  market: '#00E5A0',
  shopify_active: '#4F46E5',
  shopify_sold: '#7C3AED',
  ebay_active: '#F59E0B',
  ebay_sold: '#F97316',
  tcgplayer: '#10B981'
};

const SOURCE_ORDER = [
  'market',
  'shopify_active',
  'shopify_sold',
  'ebay_active',
  'ebay_sold',
  'tcgplayer'
];

const GRADE_BUCKET_LABELS = {
  grade_7: 'Grade 7',
  grade_8: 'Grade 8',
  grade_9: 'Grade 9',
  grade_10: 'Grade 10'
};

const buildPricingContext = (bucket, finish, gradeBucket) => {
  const normalizedBucket = bucket === 'graded' ? 'graded' : 'raw';
  return {
    bucket: normalizedBucket,
    finish: normalizedBucket === 'raw' ? (finish || 'general') : null,
    grade_bucket: normalizedBucket === 'graded' ? (gradeBucket || 'grade_9') : null,
  };
};

const isInitialDataCompatible = (data, bucket, finish, gradeBucket) => {
  if (!data) return false;

  const expected = buildPricingContext(bucket, finish, gradeBucket);
  const context = data?.__pricing_context;

  if (context && typeof context === 'object') {
    const normalized = buildPricingContext(
      context.bucket,
      context.finish,
      context.grade_bucket
    );
    return normalized.bucket === expected.bucket
      && normalized.finish === expected.finish
      && normalized.grade_bucket === expected.grade_bucket;
  }

  // Legacy bootstrap payloads are always raw/general.
  return expected.bucket === 'raw' && expected.finish === 'general';
};

const PriceHistoryModal = ({ 
  isOpen, 
  onClose, 
  cardId, 
  cardName, 
  initialData = null,
  bucket = 'raw',
  finish = 'general',
  gradeBucket = null,
  source = 'market',
  sources = null,
  hasGradedData = false
}) => {
  const [seriesData, setSeriesData] = useState({});
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30); // Default to 1 month (WO-35: else the small chart's range)
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'table'
  const [selectedBucket, setSelectedBucket] = useState(bucket || 'raw');
  const [selectedFinish, setSelectedFinish] = useState(finish || 'general');
  const [selectedGradeBucket, setSelectedGradeBucket] = useState(gradeBucket || 'grade_9');
  const [selectedSources, setSelectedSources] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [sourcesPanelOpen, setSourcesPanelOpen] = useState(false);
  const sourcesPanelRef = useRef(null);
  const wasOpenRef = useRef(false);
  const lastCardIdRef = useRef(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const { currency, convertCadAmount, formatCurrencyAmount } = useCurrency();

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const effectiveSources = useMemo(() => {
    const base = Array.isArray(sources) && sources.length > 0 ? sources : [source || 'market'];
    const seen = new Set();
    const unique = [];
    base.forEach((item) => {
      const key = (item || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(key);
    });
    return unique.length ? unique : ['market'];
  }, [sources, source]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedSources(effectiveSources);
  }, [isOpen, cardId, effectiveSources]);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    const cardChanged = isOpen && lastCardIdRef.current !== cardId;

    if (justOpened || cardChanged) {
      setSelectedBucket(bucket || 'raw');
      setSelectedFinish(finish || 'general');
      setSelectedGradeBucket(gradeBucket || 'grade_9');
      const openPeriod = Number(initialData?.__pricing_context?.period_days || initialData?.period_days);
      if (openPeriod) setSelectedPeriod(openPeriod);   // WO-35: same range as the small chart
    }

    wasOpenRef.current = isOpen;
    if (isOpen) {
      lastCardIdRef.current = cardId;
    }
  }, [isOpen, cardId, bucket, finish, gradeBucket, initialData]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!sourcesPanelOpen) return;
    const handleClickOutside = (event) => {
      if (sourcesPanelRef.current && !sourcesPanelRef.current.contains(event.target)) {
        setSourcesPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sourcesPanelOpen]);

  const periodOptions = [
    { label: '1M', days: 30 },
    { label: '3M', days: 90 },
    { label: '6M', days: 180 },
    { label: '1Y', days: 365 },
    { label: '2Y', days: 730 },
    { label: 'ALL', days: 3650 }
  ];

  useEffect(() => {
    if (!isOpen || !cardId || selectedSources.length === 0) return;

    const abortController = new AbortController();
    const signal = abortController.signal;

    const initialSeries = {};
    const availableSet = new Set(initialData?.available_sources || []);
    if (
      initialData
      && initialData.data_points
      && initialData.data_points.length > 0
      && isInitialDataCompatible(initialData, selectedBucket, selectedFinish, selectedGradeBucket)
    ) {
      const initialPeriod = initialData.period_days || 30;
      const initialSource = initialData.source || selectedSources[0] || 'market';
      if (initialPeriod === selectedPeriod && selectedSources.includes(initialSource)) {
        initialSeries[initialSource] = initialData;
      }
    }

    const sourcesToFetch = selectedSources.filter((src) => !initialSeries[src]);

    const fetchSource = async (src) => {
      const params = new URLSearchParams();
      params.set('days', selectedPeriod);
      params.set('bucket', selectedBucket);
      params.set('source', src);
      if (selectedBucket === 'raw') {
        params.set('finish', selectedFinish || 'general');
      } else {
        params.set('grade_bucket', selectedGradeBucket || 'grade_9');
      }

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000);
      });

      const fetchPromise = fetch(`${apiUrl}/api/pricing/${cardId}/history?${params.toString()}`, { signal });
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      if (signal.aborted) return { source: src, data: null };

      if (!response.ok) {
        if (response.status === 404 || response.status === 500) {
          return { source: src, data: null };
        }
        throw new Error(`Failed to fetch price data: ${response.status}`);
      }

      const result = await response.json();
      if (signal.aborted) return { source: src, data: null };

      if (result.success && result.data) {
        if (Array.isArray(result.data.available_sources)) {
          result.data.available_sources.forEach((item) => availableSet.add(item));
        }
        return { source: src, data: result.data };
      }
      return { source: src, data: null };
    };

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(sourcesToFetch.map((src) => fetchSource(src)));
        if (signal.aborted) return;
        const combined = { ...initialSeries };
        results.forEach(({ source: src, data }) => {
          combined[src] = data;
        });
        setSeriesData(combined);
        setAvailableSources(Array.from(availableSet));
      } catch (err) {
        if (err.name === 'AbortError') return;
        setSeriesData(initialSeries);
        setAvailableSources(Array.from(availableSet));
        setError(null);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    if (sourcesToFetch.length === 0) {
      setSeriesData(initialSeries);
      setAvailableSources(Array.from(availableSet));
      setLoading(false);
      return () => abortController.abort();
    }

    fetchAll();

    return () => {
      abortController.abort();
    };
  }, [
    cardId,
    selectedPeriod,
    apiUrl,
    isOpen,
    initialData,
    selectedBucket,
    selectedFinish,
    selectedGradeBucket,
    selectedSources,
  ]);

  const formatPriceLabel = (value) => {
    return formatCurrencyAmount(value, currency, { fallback: 'N/A' });
  };

  const formatPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 'N/A';
    return new Intl.NumberFormat('en-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  };

  const normalizeNumber = useCallback((value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }, []);

  const getPointPrice = useCallback((point) => {
    const candidates = [
      normalizeNumber(point.avg_price),
      normalizeNumber(point.median_price),
      normalizeNumber(point.min_price),
      normalizeNumber(point.max_price)
    ];
    return candidates.find((value) => value !== null) ?? null;
  }, [normalizeNumber]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateFull = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const activeSources = useMemo(() => {
    return selectedSources.filter((src) => seriesData[src]?.data_points && seriesData[src].data_points.length > 0);
  }, [selectedSources, seriesData]);

  const primarySource = activeSources[0] || selectedSources[0] || 'market';
  const primaryData = seriesData[primarySource] || null;
  const convertedSummary = useMemo(() => {
    const summary = primaryData?.summary
    if (!summary) return null
    return {
      ...summary,
      latest_price: convertCadAmount(summary.latest_price, currency),
      highest_price: convertCadAmount(summary.highest_price, currency),
      lowest_price: convertCadAmount(summary.lowest_price, currency),
      price_change: convertCadAmount(summary.price_change, currency),
    }
  }, [convertCadAmount, currency, primaryData])

  const availableSourceOptions = useMemo(() => {
    if (!availableSources || availableSources.length === 0) {
      return selectedSources.length ? selectedSources : ['market'];
    }
    return SOURCE_ORDER.filter((sourceKey) => availableSources.includes(sourceKey));
  }, [availableSources, selectedSources]);

  const allSourcesSelected = availableSourceOptions.length > 0
    && availableSourceOptions.every((src) => selectedSources.includes(src));

  useEffect(() => {
    if (availableSourceOptions.length === 0) return;
    setSelectedSources((prev) => {
      const filtered = prev.filter((src) => availableSourceOptions.includes(src));
      const fallback = availableSourceOptions.includes('market')
        ? 'market'
        : (availableSourceOptions[0] || 'market');
      const next = filtered.length ? filtered : [fallback];
      if (next.length === prev.length && next.every((value, idx) => value === prev[idx])) {
        return prev;
      }
      return next;
    });
  }, [availableSourceOptions]);

  const toggleSourceSelection = (sourceKey) => {
    const fallback = availableSourceOptions.includes('market')
      ? 'market'
      : (availableSourceOptions[0] || 'market');

    if (sourceKey === 'all') {
      if (allSourcesSelected) {
        setSelectedSources([fallback]);
      } else {
        setSelectedSources(availableSourceOptions);
      }
      return;
    }

    setSelectedSources((prev) => {
      const exists = prev.includes(sourceKey);
      const next = exists ? prev.filter((src) => src !== sourceKey) : [...prev, sourceKey];
      if (next.length === 0) {
        return [fallback];
      }
      return next;
    });
  };

  const normalizedSeriesPoints = useMemo(() => {
    const output = {};
    activeSources.forEach((src) => {
      const points = seriesData[src]?.data_points || [];
      const normalized = points
        .map((point) => {
          const convertedPoint = {
            ...point,
            avg_price: convertCadAmount(normalizeNumber(point.avg_price), currency),
            median_price: convertCadAmount(normalizeNumber(point.median_price), currency),
            min_price: convertCadAmount(normalizeNumber(point.min_price), currency),
            max_price: convertCadAmount(normalizeNumber(point.max_price), currency),
          };
          const price = getPointPrice(convertedPoint);
          if (price === null) return null;
          return {
            ...convertedPoint,
            price,
          };
        })
        .filter(Boolean);
      output[src] = normalized;
    });
    return output;
  }, [activeSources, convertCadAmount, currency, seriesData, getPointPrice, normalizeNumber]);


  const getAdvancedChart = () => {
    if (activeSources.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-cream/60">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-4">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
            <polyline points="17 6 23 6 23 12"></polyline>
          </svg>
          <p>No price data available for this card yet.</p>
          <p className="text-sm mt-1">Price tracking will begin once listings are found.</p>
        </div>
      );
    }

    const dateSet = new Set();
    activeSources.forEach((src) => {
      (normalizedSeriesPoints[src] || []).forEach((point) => {
        if (point?.date) {
          dateSet.add(point.date);
        }
      });
    });
    const dates = Array.from(dateSet).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    if (dates.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-cream/60">
          No valid price data available
        </div>
      );
    }

    const seriesMaps = {};
    activeSources.forEach((src) => {
      const map = new Map();
      (normalizedSeriesPoints[src] || []).forEach((point) => {
        map.set(point.date, point);
      });
      seriesMaps[src] = map;
    });

    let globalMin = Number.POSITIVE_INFINITY;
    let globalMax = Number.NEGATIVE_INFINITY;
    activeSources.forEach((src) => {
      (normalizedSeriesPoints[src] || []).forEach((point) => {
        const minCandidate = point.min_price ?? point.price;
        const maxCandidate = point.max_price ?? point.price;
        const lowPrice = Math.min(minCandidate, maxCandidate, point.price);
        const highPrice = Math.max(minCandidate, maxCandidate, point.price);
        globalMin = Math.min(globalMin, lowPrice);
        globalMax = Math.max(globalMax, highPrice);
      });
    });

    if (!Number.isFinite(globalMin) || !Number.isFinite(globalMax)) {
      return (
        <div className="flex items-center justify-center h-64 text-cream/60">
          No valid price data available
        </div>
      );
    }

    const priceRange = globalMax - globalMin || 1;
    const chartWidth = 640;
    const chartHeight = 320;
    const padding = { top: 20, right: 40, bottom: 40, left: 60 };
    const xStep = (chartWidth - padding.left - padding.right) / Math.max(dates.length - 1, 1);
    const plotHeight = chartHeight - padding.top - padding.bottom;

    const getX = (index) => padding.left + (index * xStep);
    const getY = (value) => padding.top + ((globalMax - value) / priceRange) * plotHeight;

    return (
      <div className="relative">
        <svg
          width="100%"
          height={chartHeight}
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="none"
          className="w-full"
          style={{ maxHeight: '320px' }}
        >
          <defs>
            <pattern id="grid" width="50" height="25" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 25" fill="none" stroke="#6A66A3" strokeWidth="0.5" opacity="0.2"/>
            </pattern>
            {activeSources.map((src) => {
              const color = SOURCE_COLORS[src] || '#00E5A0';
              return (
                <linearGradient key={src} id={`areaGradient-${src}`} x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.35 }} />
                  <stop offset="100%" style={{ stopColor: color, stopOpacity: 0.08 }} />
                </linearGradient>
              );
            })}
          </defs>

          <rect
            x={padding.left}
            y={padding.top}
            width={chartWidth - padding.left - padding.right}
            height={chartHeight - padding.top - padding.bottom}
            fill="url(#grid)"
          />

          {activeSources.map((src) => {
            const color = SOURCE_COLORS[src] || '#00E5A0';
            const map = seriesMaps[src];
            const segments = [];
            let current = [];
            dates.forEach((date, idx) => {
              const point = map.get(date);
              if (point) {
                current.push({ idx, point });
              } else if (current.length) {
                segments.push(current);
                current = [];
              }
            });
            if (current.length) segments.push(current);

            return (
              <g key={src}>
                {segments.map((segment, segIndex) => {
                  const topPath = segment.map(({ idx, point }) => {
                    const minCandidate = point.min_price ?? point.price;
                    const maxCandidate = point.max_price ?? point.price;
                    const highPrice = Math.max(minCandidate, maxCandidate, point.price);
                    return `${getX(idx)},${getY(highPrice)}`;
                  }).join(' L ');

                  const bottomPath = segment.slice().reverse().map(({ idx, point }) => {
                    const minCandidate = point.min_price ?? point.price;
                    const maxCandidate = point.max_price ?? point.price;
                    const lowPrice = Math.min(minCandidate, maxCandidate, point.price);
                    return `${getX(idx)},${getY(lowPrice)}`;
                  }).join(' L ');

                  const areaPath = segment.length > 1 ? `M ${topPath} L ${bottomPath} Z` : '';

                  const linePath = segment.map(({ idx, point }) => `${getX(idx)},${getY(point.price)}`).join(' L ');
                  const lineData = segment.length > 0 ? `M ${linePath}` : '';

                  return (
                    <g key={`${src}-seg-${segIndex}`}>
                      {areaPath && (
                        <path
                          d={areaPath}
                          fill={`url(#areaGradient-${src})`}
                          opacity="0.7"
                        />
                      )}
                      {lineData && (
                        <path
                          d={lineData}
                          fill="none"
                          stroke={color}
                          strokeWidth="2.5"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                      )}
                    </g>
                  );
                })}

                {segments.flat().map(({ idx, point }, index) => {
                  const tooltipKey = `${src}-${idx}`;
                  const isActive = activeTooltip === tooltipKey;
                  return (
                    <g key={`${src}-pt-${index}`}>
                      <circle
                        cx={getX(idx)}
                        cy={getY(point.price)}
                        r={isActive ? 6 : 4}
                        fill={isActive ? '#fff' : color}
                        stroke={isActive ? color : '#1A1A20'}
                        strokeWidth="2"
                        style={{ transition: 'r 0.15s, fill 0.15s' }}
                      />
                      <circle
                        cx={getX(idx)}
                        cy={getY(point.price)}
                        r="12"
                        fill="transparent"
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTooltip(isActive ? null : tooltipKey);
                        }}
                      />
                      {isActive && (
                        <foreignObject
                          x={getX(idx) - 100}
                          y={getY(point.price) - 80}
                          width="200"
                          height="70"
                          style={{ overflow: 'visible', pointerEvents: 'none' }}
                        >
                          <div style={{
                            background: '#1A1A20',
                            border: '1px solid rgba(106, 102, 163, 0.5)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            fontSize: '11px',
                            color: '#E8E0D0',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: '2px' }}>{formatDateFull(point.date)}</div>
                            <div style={{ color: SOURCE_COLORS[src] || '#00E5A0' }}>
                              {SOURCE_LABELS[src] || src}: {formatPriceLabel(point.price)}
                            </div>
                            <div style={{ color: 'rgba(232,224,208,0.6)' }}>
                              {point.listing_count} listings
                              {point.min_price != null && point.max_price != null
                                ? ` • ${formatPriceLabel(point.min_price)} – ${formatPriceLabel(point.max_price)}`
                                : ''}
                            </div>
                          </div>
                        </foreignObject>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
            const price = globalMin + (priceRange * ratio);
            const y = padding.top + ((1 - ratio) * plotHeight);
            return (
              <g key={`y-${index}`}>
                <line
                  x1={padding.left - 5}
                  y1={y}
                  x2={padding.left}
                  y2={y}
                  stroke="#6A66A3"
                  strokeWidth="1"
                />
                <text
                  x={padding.left - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-cream/60 text-xs"
                >
                  {formatPriceLabel(price)}
                </text>
              </g>
            );
          })}

          {dates.map((date, index) => {
            if (index % Math.ceil(dates.length / 5) !== 0 && index !== dates.length - 1) return null;
            const x = getX(index);
            const y = chartHeight - padding.bottom + 15;
            return (
              <g key={`x-${date}`}>
                <line
                  x1={x}
                  y1={chartHeight - padding.bottom}
                  x2={x}
                  y2={chartHeight - padding.bottom + 5}
                  stroke="#6A66A3"
                  strokeWidth="1"
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  className="fill-cream/60 text-xs"
                >
                  {formatDate(date)}
                </text>
              </g>
            );
          })}
        </svg>
        {/* Click anywhere on chart area to dismiss tooltip */}
        {activeTooltip && (
          <div
            className="absolute inset-0 z-10"
            onClick={() => setActiveTooltip(null)}
          />
        )}
      </div>
    );
  };

  const getDataTable = () => {
    const rows = [];
    activeSources.forEach((src) => {
      (normalizedSeriesPoints[src] || []).forEach((point) => {
        rows.push({
          source: src,
          date: point.date,
          price: point.price,
          min_price: point.min_price,
          max_price: point.max_price,
          listing_count: point.listing_count,
        });
      });
    });

    if (!rows.length) {
      return (
        <div className="text-center text-cream/60 py-8">
          No price data available
        </div>
      );
    }

    const sortedData = rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
      <div className="overflow-y-auto max-h-64">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-dark border-b border-purple/30">
            <tr>
              <th className="text-left py-2 px-3 text-cream/80">Date</th>
              <th className="text-left py-2 px-3 text-cream/80">Source</th>
              <th className="text-right py-2 px-3 text-cream/80">Avg Price</th>
              <th className="text-right py-2 px-3 text-cream/80">Low</th>
              <th className="text-right py-2 px-3 text-cream/80">High</th>
              <th className="text-right py-2 px-3 text-cream/80">Listings</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((point, index) => (
              <tr key={`${point.source}-${index}`} className="border-b border-purple/20 hover:bg-purple/10">
                <td className="py-2 px-3 text-cream">{formatDate(point.date)}</td>
                <td className="py-2 px-3 text-cream/80">{SOURCE_LABELS[point.source] || point.source}</td>
                <td className="py-2 px-3 text-right text-green font-medium">
                  {formatPriceLabel(point.price)}
                </td>
                <td className="py-2 px-3 text-right text-cream/80">
                  {formatPriceLabel(point.min_price)}
                </td>
                <td className="py-2 px-3 text-right text-cream/80">
                  {formatPriceLabel(point.max_price)}
                </td>
                <td className="py-2 px-3 text-right text-cream/80">
                  {point.listing_count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark border border-purple/30 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-purple/30">
          <div>
            <h2 className="text-xl font-medium text-cream">Price History</h2>
            <p className="text-cream/60 text-sm mt-1">{cardName}</p>
          </div>
          <button 
            onClick={onClose}
            className="text-cream/60 hover:text-cream transition p-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Controls */}
          <div className="flex flex-col gap-4 mb-6">
            {/* Period selector */}
            <div className="flex flex-wrap gap-2">
              {periodOptions.map((period) => {
                // WO-35: ranges are never disabled (the loaded range's span disabled every longer one).
                return (
                  <button
                    key={period.days}
                    onClick={() => {
                      setSelectedPeriod(period.days);
                    }}
                    className={`px-3 py-1 text-sm rounded transition ${
                      selectedPeriod === period.days
                        ? 'bg-purple text-cream'
                        : 'bg-purple/20 text-cream/60 hover:text-cream hover:bg-purple/40'
                    }`}
                    type="button"
                  >
                    {period.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* View mode toggle */}
              <div className="flex bg-purple/20 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('chart')}
                  className={`px-3 py-1 text-sm rounded transition flex items-center ${
                    viewMode === 'chart'
                      ? 'bg-purple text-cream'
                      : 'text-cream/60 hover:text-cream'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                    <polyline points="17 6 23 6 23 12"></polyline>
                  </svg>
                  Chart
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 text-sm rounded transition flex items-center ${
                    viewMode === 'table'
                      ? 'bg-purple text-cream'
                      : 'text-cream/60 hover:text-cream'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="3" y1="15" x2="21" y2="15"></line>
                    <line x1="12" y1="3" x2="12" y2="21"></line>
                  </svg>
                  Table
                </button>
              </div>

              {hasGradedData && (
                <div className="flex items-center bg-dark border border-purple/30 rounded-lg p-1">
                  <button
                    onClick={() => setSelectedBucket('raw')}
                    className={`px-3 py-1 text-xs rounded transition ${
                      selectedBucket === 'raw'
                        ? 'bg-purple text-cream'
                        : 'text-cream/60 hover:text-cream'
                    }`}
                  >
                    Ungraded
                  </button>
                  <button
                    onClick={() => setSelectedBucket('graded')}
                    className={`px-3 py-1 text-xs rounded transition ${
                      selectedBucket === 'graded'
                        ? 'bg-purple text-cream'
                        : 'text-cream/60 hover:text-cream'
                    }`}
                  >
                    Graded
                  </button>
                </div>
              )}

              {selectedBucket === 'graded' ? (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-cream/60">Grade</label>
                  <select
                    value={selectedGradeBucket || ''}
                    onChange={(e) => setSelectedGradeBucket(e.target.value)}
                    className="bg-dark border border-purple/30 rounded px-2 py-1 text-xs text-cream"
                  >
                    {Object.keys(GRADE_BUCKET_LABELS).map((bucketKey) => (
                      <option key={bucketKey} value={bucketKey}>
                        {GRADE_BUCKET_LABELS[bucketKey]}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-cream/60">Finish</label>
                  <select
                    value={selectedFinish}
                    onChange={(e) => setSelectedFinish(e.target.value)}
                    className="bg-dark border border-purple/30 rounded px-2 py-1 text-xs text-cream"
                  >
                    <option value="general">General</option>
                    <option value="holofoil">Holofoil</option>
                    <option value="reverse">Reverse</option>
                  </select>
                </div>
              )}

              <div className="ml-auto relative">
                <button
                  type="button"
                  onClick={() => setSourcesPanelOpen((prev) => !prev)}
                  className="flex items-center gap-2 px-3 py-1 rounded-lg border border-purple/30 bg-dark text-xs text-cream/80 hover:text-cream hover:bg-purple/10 transition"
                >
                  Sources
                </button>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden">
            {sourcesPanelOpen && (
              <div
                className="absolute inset-0 z-20 bg-black/40"
                onClick={() => setSourcesPanelOpen(false)}
              />
            )}
            {/* Content */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple"></div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64 text-red-400">
                Error: {error}
              </div>
            ) : (
              <>
                {viewMode === 'chart' ? getAdvancedChart() : getDataTable()}

                {/* Summary Stats */}
                {primaryData && convertedSummary && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-purple/30">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green">
                        {formatPriceLabel(convertedSummary.latest_price)}
                      </div>
                      <div className="text-cream/60 text-sm">Current Price</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${
                        convertedSummary.price_change >= 0 ? 'text-green' : 'text-red-400'
                      }`}>
                        {convertedSummary.price_change >= 0 ? '+' : ''}
                        {formatPercent(primaryData.summary?.price_change_percent)}%
                      </div>
                      <div className="text-cream/60 text-sm">
                        {periodOptions.find(p => p.days === selectedPeriod)?.label} Change
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-cream">
                        {formatPriceLabel(convertedSummary.highest_price)}
                      </div>
                      <div className="text-cream/60 text-sm">Period High</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-cream">
                        {formatPriceLabel(convertedSummary.lowest_price)}
                      </div>
                      <div className="text-cream/60 text-sm">Period Low</div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div
              ref={sourcesPanelRef}
              className={`absolute right-0 top-0 z-30 h-full w-64 border-l border-purple/30 bg-[#15151d] shadow-xl transition-transform duration-200 ${
                sourcesPanelOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'
              }`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-purple/20">
                <span className="text-xs uppercase tracking-wide text-cream/60">Sources</span>
                <button
                  type="button"
                  onClick={() => setSourcesPanelOpen(false)}
                  className="text-cream/60 hover:text-cream text-sm"
                >
                  Close
                </button>
              </div>
              <div className="p-4 space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSourceSelection('all')}
                  className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-xs transition ${
                    allSourcesSelected
                      ? 'bg-purple text-cream'
                      : 'bg-dark text-cream/70 hover:text-cream'
                  }`}
                >
                  <span>Show All</span>
                  <span className="text-[10px]">{allSourcesSelected ? 'On' : 'Off'}</span>
                </button>
                {availableSourceOptions.map((sourceKey) => {
                  const active = selectedSources.includes(sourceKey);
                  return (
                    <button
                      key={sourceKey}
                      type="button"
                      onClick={() => toggleSourceSelection(sourceKey)}
                      className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-xs transition ${
                        active ? 'bg-purple/20 text-cream' : 'bg-dark text-cream/60 hover:text-cream'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: SOURCE_COLORS[sourceKey] || '#6B7280' }} />
                        {SOURCE_LABELS[sourceKey] || sourceKey}
                      </span>
                      <span className="text-[10px]">{active ? 'On' : 'Off'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceHistoryModal;
